import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface LogRow {
  id: string;
  agentId: string | null;
  event: string;
  metadata: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
}

export function AdminAuditLogPage(): React.ReactElement {
  useDocumentTitle('Audit log');
  const { agent } = useAuth();
  const [logs, setLogs] = useState<LogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState('');

  async function load() {
    try {
      const p = new URLSearchParams();
      if (event.trim()) p.set('event', event.trim());
      p.set('limit', '200');
      const r = await apiFetch<{ logs: LogRow[] }>(`/api/admin/audit-logs?${p.toString()}`);
      setLogs(r.logs);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load audit log.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (!agent || agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">Audit log</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Append-only record of security-relevant agent actions. Most recent 200 shown.
        </p>
      </div>

      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void load();
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <FormField
            label="Filter by event"
            name="event"
            placeholder="e.g. agent.demoted"
            value={event}
            onChange={(e) => setEvent(e.target.value)}
          />
          <button
            type="submit"
            className="rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-4 py-2.5 text-sm font-medium text-white hover:bg-[var(--color-t3-blue)]/90"
          >
            Apply
          </button>
        </form>
      </Card>

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

      {logs === null ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : logs.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-[var(--color-muted)]">No matching audit entries.</p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Metadata</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-[var(--color-border)] align-top">
                  <td className="px-4 py-2 font-mono text-xs text-[var(--color-muted)]">
                    {new Date(l.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{l.event}</td>
                  <td className="px-4 py-2 font-mono text-xs text-[var(--color-muted)]">
                    {l.agentId?.slice(0, 8) ?? '—'}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-[var(--color-muted)]">{l.ip ?? '—'}</td>
                  <td className="px-4 py-2 text-xs">
                    {Object.keys(l.metadata).length > 0 ? (
                      <details>
                        <summary className="cursor-pointer text-[var(--color-t3-blue)]">view</summary>
                        <pre className="mt-1 overflow-auto rounded bg-[var(--color-surface)] p-2 text-[11px]">
                          {JSON.stringify(l.metadata, null, 2)}
                        </pre>
                      </details>
                    ) : (
                      <span className="text-[var(--color-muted)]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
